import { useState, useEffect, useMemo } from 'react'
import type React from 'react'
import {
  PlusCircle,
  Smartphone,
  ArrowRightLeft,
  Search,
  CheckCircle,
  QrCode,
  Moon,
  Sun,
  Monitor,
  Info
} from 'lucide-react'
import type { Produto, Lote, LocalArmazenamento, Saldo, Movimentacao, Entidade, SystemParameters } from './types'
import { gerarCodigoLote, getLoteSpecsString, sanitizeInput } from './types'
import type { Usuario, PermissoesMatriz } from './App'

function FieldTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  return (
    <span 
      style={{ 
        position: 'relative', 
        display: 'inline-flex', 
        alignItems: 'center', 
        marginLeft: '6px', 
        cursor: 'pointer',
        verticalAlign: 'middle'
      }} 
      onMouseEnter={() => setShow(true)} 
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}
    >
      <Info size={13} style={{ color: 'var(--text-muted)', opacity: 0.6 }} />
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '22px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          width: '220px',
          whiteSpace: 'normal',
          zIndex: 9999,
          color: 'var(--text-high)',
          lineHeight: '1.4',
          fontWeight: 'normal',
          pointerEvents: 'none'
        }}>
          {text}
        </div>
      )}
    </span>
  )
}

interface MobileAppProps {
  produtos: Produto[]
  setProdutos: React.Dispatch<React.SetStateAction<Produto[]>>
  lotes: Lote[]
  setLotes: React.Dispatch<React.SetStateAction<Lote[]>>
  locais: LocalArmazenamento[]
  setLocais: React.Dispatch<React.SetStateAction<LocalArmazenamento[]>>
  movimentacoes: Movimentacao[]
  setMovimentacoes: React.Dispatch<React.SetStateAction<Movimentacao[]>>
  saldos: Saldo[]
  theme: 'light' | 'dark'
  setTheme: React.Dispatch<React.SetStateAction<'light' | 'dark'>>
  entidades: Entidade[]
  parametros: SystemParameters
  setParametros: React.Dispatch<React.SetStateAction<SystemParameters>>
  usuarioLogado: Usuario | null
  setUsuarioLogado: React.Dispatch<React.SetStateAction<Usuario | null>>
  usuarios: Usuario[]
  permissoes: PermissoesMatriz
}

export default function MobileApp({
  produtos,
  setProdutos,
  lotes,
  setLotes,
  locais,
  setLocais: _setLocais,
  movimentacoes: _movimentacoes,
  setMovimentacoes,
  saldos,
  theme,
  setTheme,
  entidades,
  parametros,
  setParametros: _setParametros,
  usuarioLogado,
  setUsuarioLogado,
  usuarios,
  permissoes
}: MobileAppProps) {
  // Mobile / PWA States
  const [mobileScreen, setMobileScreen] = useState<'menu' | 'entrada' | 'saida' | 'consulta'>(() => {
    return (localStorage.getItem('mobile_active_screen') as any) || 'menu'
  })

  useEffect(() => {
    localStorage.setItem('mobile_active_screen', mobileScreen)
  }, [mobileScreen])
  const [mobileScannerActive, setMobileScannerActive] = useState(false)
  const [mobileScannedCode, setMobileScannedCode] = useState('')
  const [mobileMessage, setMobileMessage] = useState('')
  const [mProdId, setMProdId] = useState(produtos[0]?.id || '')
  const [mProdName, setMProdName] = useState(() => {
    const p = produtos.find(x => x.id === (produtos[0]?.id || ''))
    return p ? p.nome : ''
  })
  const [mLocalId, setMLocalId] = useState(locais[0]?.id || '')
  const [mLocalCode, setMLocalCode] = useState(() => {
    const l = locais.find(x => x.id === (locais[0]?.id || ''))
    return l ? l.codigo_local : ''
  })
  const [, setMFornId] = useState('')
  const [mFornName, setMFornName] = useState('')
  const [mLoteCode, setMLoteCode] = useState('')
  const [mTom, setMTom] = useState('')
  const [mCor, setMCor] = useState(() => parametros.representacoesComerciais[0] || 'BEGE')
  const [mTipoMadeira, setMTipoMadeira] = useState<string>(() => (parametros.grupos.find(g => g.nome === 'MADEIRA')?.subgrupos[0]) || 'EUCALIPTO')
  const [mQty, setMQty] = useState(10)
  const [generatedZPL, setGeneratedZPL] = useState('')

  // Login States
  const [loginUser, setLoginUser] = useState('')
  const [loginPass, setLoginPass] = useState('')
  const [loginErr, setLoginErr] = useState('')

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!loginUser.trim() || !loginPass.trim()) return

    const match = usuarios.find(u => u.username.toUpperCase() === loginUser.trim().toUpperCase())
    if (match && match.senha === loginPass) {
      setUsuarioLogado(match)
      setLoginUser('')
      setLoginPass('')
      setLoginErr('')
    } else {
      setLoginErr('Usuário ou senha incorretos.')
    }
  }



  // Listas de valores únicos para autocomplete/datalist (Sanitizados e sem duplicatas) — Memoizados
  const uniqueTons = useMemo(() => Array.from(new Set(lotes.map(l => l.tom_especifico).filter((t): t is string => !!t).map(t => sanitizeInput(t)))), [lotes])
  const uniqueCores = useMemo(() => Array.from(new Set([
    ...parametros.representacoesComerciais.map(c => sanitizeInput(c)),
    ...lotes.map(l => l.cor).filter((c): c is string => !!c).map(c => sanitizeInput(c))
  ])), [parametros.representacoesComerciais, lotes])
  const uniqueMarcas = useMemo(() => Array.from(new Set(lotes.map(l => l.marca).filter((m): m is string => !!m).map(m => sanitizeInput(m)))), [lotes])
  const uniqueMadeiras = useMemo(() => Array.from(new Set([
    ...(parametros.grupos.find(g => g.nome === 'MADEIRA')?.subgrupos || []).map(t => sanitizeInput(t))
  ])), [parametros.grupos])

  // Wood Calculator States
  const [calcMode, setCalcMode] = useState<'volume_recibido' | 'pallet_dims' | 'pecas_pallets'>('pecas_pallets')
  const [calcPallets, setCalcPallets] = useState<number>(1)
  const [calcStackH, setCalcStackH] = useState<number>(120) // in cm
  const [calcStackW, setCalcStackW] = useState<number>(100) // in cm
  const [calcStackL, setCalcStackL] = useState<number>(180) // in cm
  const [calcVolumeTotal, setCalcVolumeTotal] = useState<number>(1.0)
  const [calcPecasTotal, setCalcPecasTotal] = useState<number>(200)
  const [mCheckoutQtyUnits, setMCheckoutQtyUnits] = useState<number>(1)
  const [mCheckoutQtyVolume, setMCheckoutQtyVolume] = useState<number>(0)
  const [mCusto, setMCusto] = useState<number>(0)
  const [mVenda, setMVenda] = useState<number>(0)

  useEffect(() => {
    setMVenda(mCusto > 0 ? Number((mCusto * 1.3).toFixed(2)) : 0)
  }, [mCusto])

  // Sync state drop-downs on product list updates
  useEffect(() => {
    if (produtos.length > 0) {
      if (!mProdId) {
        setMProdId(produtos[0].id)
        setMProdName(produtos[0].nome)
      }
    }
  }, [produtos])

  useEffect(() => {
    if (locais.length > 0) {
      if (!mLocalId) {
        setMLocalId(locais[0].id)
        setMLocalCode(locais[0].codigo_local)
      }
    }
  }, [locais])

  // Auto-generate Lote code in Mobile Inbound when product or color updates
  useEffect(() => {
    const prod = produtos.find(p => p.id === mProdId || p.nome.toUpperCase() === mProdName.toUpperCase())
    if (prod) {
      const generated = gerarCodigoLote(
        prod.grupo,
        prod.subgrupo
      )
      setMLoteCode(generated)
    }
  }, [mProdId, mProdName, produtos])

  // Wood Calculator Computations
  const selectedProduct = produtos.find(p => p.id === mProdId || p.nome.toUpperCase() === mProdName.toUpperCase())
  
  let computedUnitsPerPallet = 200
  let computedTotalUnits = 200
  let computedTotalVolume = 0.0
  let computedTotalArea = 0.0

  if (selectedProduct && selectedProduct.grupo.toUpperCase() === 'MADEIRA') {
    const match = selectedProduct.nome.match(/(\d+)\s*[xX]\s*(\d+)\s*[xX]\s*([\d.,]+)\s*cm/)
    const L = match ? Number(match[1]) / 100 : 1.86
    const W = match ? Number(match[2]) / 100 : 1.36
    const H = match ? parseFloat(match[3].replace(',', '.')) / 100 : 0.005
    const vUnit = L * W * H
    const aUnit = L * W

    if (calcMode === 'volume_recibido') {
      computedTotalVolume = calcVolumeTotal
      computedTotalUnits = vUnit > 0 ? Math.round(calcVolumeTotal / vUnit) : 0
      computedUnitsPerPallet = calcPallets > 0 ? Math.round(computedTotalUnits / calcPallets) : 0
      computedTotalArea = computedTotalUnits * aUnit
    } else if (calcMode === 'pallet_dims') {
      const stackL_m = calcStackL / 100
      const stackW_m = calcStackW / 100
      const stackH_m = calcStackH / 100
      
      const nL = L > 0 ? Math.max(1, Math.floor(stackL_m / L)) : 1
      const nW = W > 0 ? Math.max(1, Math.floor(stackW_m / W)) : 1
      const nH = H > 0 ? Math.max(1, Math.floor(stackH_m / H)) : 1
      
      computedUnitsPerPallet = nL * nW * nH
      computedTotalUnits = computedUnitsPerPallet * calcPallets
      computedTotalVolume = computedTotalUnits * vUnit
      computedTotalArea = computedTotalUnits * aUnit
    } else if (calcMode === 'pecas_pallets') {
      computedTotalUnits = calcPecasTotal
      computedUnitsPerPallet = calcPallets > 0 ? Math.round(calcPecasTotal / calcPallets) : 0
      computedTotalVolume = computedTotalUnits * vUnit
      computedTotalArea = computedTotalUnits * aUnit
    }
  }

  const triggerScan = (code: string) => {
    if (mobileScreen === 'entrada') {
      const matchLocal = locais.find(l => l.codigo_local === code)
      if (matchLocal) {
        setMLocalId(matchLocal.id)
        setMLocalCode(matchLocal.codigo_local)
      }
    } else {
      setMobileScannedCode(code)
    }
    setMobileScannerActive(false)
  }

  const handleMobileEntrada = (e: React.FormEvent) => {
    e.preventDefault()
    if (!mLoteCode) return

    // 1. Validar e Resolver Produto
    const prodNameClean = sanitizeInput(mProdName)
    const existingP = produtos.find(p => p.nome.toUpperCase() === prodNameClean)
    if (!existingP) {
      alert(`Erro: Material "${prodNameClean}" não cadastrado. Por favor, cadastrar o item na aba "Cadastro de Material" antes de efetuar a entrada.`)
      return
    }
    const finalProdId = existingP.id
    const product = existingP

    // 2. Validar e Resolver Local
    const localCodeClean = sanitizeInput(mLocalCode)
    const existingL = locais.find(l => l.codigo_local.toUpperCase() === localCodeClean)
    if (!existingL) {
      alert(`Erro: Local/Endereço "${localCodeClean}" não cadastrado. Por favor, cadastre a posição de estoque antes de efetuar a entrada.`)
      return
    }
    const finalLocalId = existingL.id
    const localCode = existingL.codigo_local

    // 3. Validar e Resolver Fornecedor
    let finalFornId = ''
    if (mFornName.trim()) {
      const fornNameClean = sanitizeInput(mFornName)
      const existingF = entidades.find(ent => ent.nome.toUpperCase() === fornNameClean && ent.tipo_relacao === 'fornecedor')
      if (!existingF) {
        alert(`Erro: Fornecedor "${fornNameClean}" não cadastrado. Por favor, cadastre o parceiro antes de efetuar a entrada.`)
        return
      }
      finalFornId = existingF.id
    } else {
      alert('Erro: Fornecedor é obrigatório para esta operação.')
      return
    }

    // 4. Validar Parâmetros Customizáveis
    const grpUpper = product.grupo.toUpperCase()
    if (grpUpper === 'MADEIRA' && mTipoMadeira) {
      const cleanWoodType = sanitizeInput(mTipoMadeira)
      const allowedMadeira = (parametros.grupos.find(g => g.nome === 'MADEIRA')?.subgrupos || []).map(t => t.toUpperCase())
      if (!allowedMadeira.includes(cleanWoodType)) {
        alert(`Erro: Tipo de Madeira "${cleanWoodType}" não cadastrado. Registre-o em Parâmetros.`)
        return
      }
    }

    if (grpUpper === 'TECIDO' && mCor) {
      const cleanCor = sanitizeInput(mCor)
      if (!parametros.representacoesComerciais.map(c => c.toUpperCase()).includes(cleanCor)) {
        alert(`Erro: Representação Comercial "${cleanCor}" não cadastrada em Parâmetros.`)
        return
      }
    }

    let cleanLoteCode = sanitizeInput(mLoteCode)
    if (!cleanLoteCode) {
      cleanLoteCode = gerarCodigoLote(
        product.grupo,
        product.subgrupo
      )
    }

    // Update product's purchase and sale price (último valor atual)
    setProdutos(prev => prev.map(p => p.id === product.id ? {
      ...p,
      preco_compra: mCusto,
      preco_venda: mVenda
    } : p))

    if (grpUpper === 'MADEIRA') {
      const match = product.nome.match(/(\d+)\s*[xX]\s*(\d+)\s*[xX]\s*([\d.,]+)\s*cm/)
      const L = match ? Number(match[1]) / 100 : 1.86
      const W = match ? Number(match[2]) / 100 : 1.36
      const H = match ? parseFloat(match[3].replace(',', '.')) / 100 : 0.005
      const vUnit = L * W * H

      let finalUnits = 0
      let numPallets = 1

      if (calcMode === 'pallet_dims') {
        const stackL_m = calcStackL / 100
        const stackW_m = calcStackW / 100
        const stackH_m = calcStackH / 100
        const nL = L > 0 ? Math.max(1, Math.floor(stackL_m / L)) : 1
        const nW = W > 0 ? Math.max(1, Math.floor(stackW_m / W)) : 1
        const nH = H > 0 ? Math.max(1, Math.floor(stackH_m / H)) : 1
        const unitsPerPallet = nL * nW * nH
        numPallets = calcPallets
        finalUnits = unitsPerPallet * numPallets
      } else if (calcMode === 'volume_recibido') {
        finalUnits = vUnit > 0 ? Math.round(calcVolumeTotal / vUnit) : 0
        numPallets = calcPallets
      } else if (calcMode === 'pecas_pallets') {
        finalUnits = calcPecasTotal
        numPallets = calcPallets
      }

      if (numPallets <= 0) numPallets = 1

      const unitsPerPalletFloat = finalUnits / numPallets
      let zplAccumulator = ''
      const newLotesList: Lote[] = []
      const newMovsList: Movimentacao[] = []

      for (let i = 1; i <= numPallets; i++) {
        const palletUnits = i === numPallets 
          ? finalUnits - (Math.floor(unitsPerPalletFloat) * (numPallets - 1))
          : Math.floor(unitsPerPalletFloat)

        const palletVolume = palletUnits * vUnit
        const subLoteCode = `${cleanLoteCode}-P${i}`
        const subLoteId = `lote-${Date.now()}-P${i}`

        const loteEntry: Lote = {
          id: subLoteId,
          codigo_lote: subLoteCode,
          produto_id: finalProdId,
          tom_especifico: 'Padrão Cru',
          tipo_madeira: sanitizeInput(mTipoMadeira),
          quantidade_nota: palletVolume,
          custo_unitario_compra: mCusto,
          data_recebimento: new Date().toISOString(),
          fornecedor_id: finalFornId || undefined
        }
        newLotesList.push(loteEntry)

        const movEntry: Movimentacao = {
          id: `mov-${Date.now()}-P${i}`,
          data_movimento: new Date().toISOString(),
          produto_id: finalProdId,
          lote_id: subLoteId,
          local_destino_id: finalLocalId,
          tipo_movimento: 'entrada',
          motivo: 'compra',
          quantidade: palletVolume,
          usuario: 'Operador Mobile'
        }
        newMovsList.push(movEntry)

        zplAccumulator += `^XA
^CF0,25
^FO40,40^FDTHOTH WMS - PRINCE CAMA BOX^FS
^FO40,70^FDINBOUND: PALLET DE MADEIRA [${sanitizeInput(mTipoMadeira)}]^FS
^FO40,100^FDLote: ${subLoteCode}^FS
^FO40,130^FDQtd: ${palletUnits} PECAS (${palletVolume.toFixed(4)} m3)^FS
^FO40,160^FDLocal: ${localCode}^FS
^FO40,190^FDPallet: ${i} de ${numPallets}^FS
^FO460,40^BQN,2,4^FDQA,${subLoteCode}^FS
^BY2,2,60
^FO40,220^BCN,60,Y,N,N^FD${subLoteCode}^FS
^XZ
`
      }

      setLotes(prev => [...prev, ...newLotesList])
      setMovimentacoes(prev => [...prev, ...newMovsList])
      setGeneratedZPL(zplAccumulator)
      setMobileMessage(`Entrada de ${finalUnits} unidades (${numPallets} pallets) registrada com sucesso!`)
    } else {
      const subLoteId = `lote-${Date.now()}`
      const loteEntry: Lote = {
        id: subLoteId,
        codigo_lote: cleanLoteCode,
        produto_id: finalProdId,
        tom_especifico: sanitizeInput(mTom) || 'Padrão',
        tipo_madeira: undefined,
        quantidade_nota: mQty,
        custo_unitario_compra: mCusto,
        data_recebimento: new Date().toISOString(),
        fornecedor_id: finalFornId || undefined
      }

      const movEntry: Movimentacao = {
        id: `mov-${Date.now()}`,
        data_movimento: new Date().toISOString(),
        produto_id: finalProdId,
        lote_id: subLoteId,
        local_destino_id: finalLocalId,
        tipo_movimento: 'entrada',
        motivo: 'compra',
        quantidade: mQty,
        usuario: 'Operador Mobile'
      }

      const zpl = `^XA
^CF0,25
^FO40,40^FDTHOTH WMS - PRINCE CAMA BOX^FS
^FO40,75^FDINBOUND: RECEBIMENTO GERAL^FS
^FO40,110^FDLote: ${cleanLoteCode}^FS
^FO40,140^FDQtd: ${mQty} ${product.unidade_medida_padrao.toUpperCase()}^FS
^FO40,170^FDLocal: ${localCode}^FS
^FO460,40^BQN,2,4^FDQA,${cleanLoteCode}^FS
^BY2,2,80
^FO40,210^BCN,80,Y,N,N^FD${cleanLoteCode}^FS
^XZ
`

      setLotes(prev => [...prev, loteEntry])
      setMovimentacoes(prev => [...prev, movEntry])
      setGeneratedZPL(zpl)
      setMobileMessage(`Entrada de ${mQty} ${product.unidade_medida_padrao.toUpperCase()} registrada com sucesso!`)
    }

    setMLoteCode('')
    setMTom('')
    setMQty(10)

    setTimeout(() => {
      setMobileMessage('')
      setGeneratedZPL('')
      setMobileScreen('menu')
    }, 6000)
  }

  const handleMobileSaida = (loteCode: string, qtyToRemove: number, motivo: 'producao' | 'perda' | 'venda') => {
    const lote = lotes.find(l => l.codigo_lote === loteCode)
    if (!lote) return

    const product = produtos.find(p => p.id === lote.produto_id)
    if (!product) return

    const localSaldo = saldos.find(s => s.lote_id === lote.id)
    const localId = localSaldo ? localSaldo.local_id : 'local-gaiola-t1'
    const physicalQty = localSaldo ? localSaldo.quantidade_fisica : 0

    if (qtyToRemove > physicalQty + 0.00001) {
      alert(`Quantidade insuficiente! Saldo atual deste lote/pallet: ${physicalQty}`)
      return
    }

    const movId = `mov-${Date.now()}`
    const movement: Movimentacao = {
      id: movId,
      data_movimento: new Date().toISOString(),
      produto_id: lote.produto_id,
      lote_id: lote.id,
      local_origem_id: localId,
      tipo_movimento: 'saida',
      motivo,
      quantidade: qtyToRemove,
      usuario: 'Operador Mobile'
    }

    setMovimentacoes(prev => [...prev, movement])



    setMobileMessage(`Baixa de ${qtyToRemove.toFixed(4)} ${product.unidade_medida_padrao} realizada com sucesso!`)
    setMobileScannedCode('')
    setTimeout(() => {
      setMobileMessage('')
      setMobileScreen('menu')
    }, 3000)
  }

  const renderMobileScreen = () => {
    if (mobileScannerActive) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', minHeight: '80vh', justifyContent: 'center', padding: '20px' }}>
          <div style={{ position: 'relative', width: '280px', height: '280px', border: '3px solid var(--color-primary)', borderRadius: '24px', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <QrCode size={64} style={{ color: 'var(--color-primary)', opacity: 0.6 }} className="pulse-glow" />
              <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>Buscando QR Code...</div>
              <div style={{ position: 'absolute', left: 0, right: 0, height: '2px', background: 'var(--color-primary)', animation: 'pulse-glow 1.5s infinite' }}></div>
            </div>
          </div>
          
          <div style={{ fontSize: '13px', color: 'var(--text-high)', textAlign: 'center', padding: '0 20px' }}>
            Simular leitura escaneando um dos seguintes códigos de exemplo:
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', maxWidth: '300px' }}>
            {mobileScreen === 'entrada' ? (
              locais.map(g => (
                <button key={g.id} className="btn-secondary" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => triggerScan(g.codigo_local)}>
                  Local {g.codigo_local}
                </button>
              ))
            ) : (
              lotes.map(l => (
                <button key={l.id} className="btn-secondary" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => triggerScan(l.codigo_lote)}>
                  Lote {l.codigo_lote}
                </button>
              ))
            )}
          </div>
          
          <button className="btn-secondary" style={{ marginTop: '16px', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={() => setMobileScannerActive(false)}>Cancelar</button>
        </div>
      )
    }

    if (mobileMessage) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: '16px', textAlign: 'center', padding: '24px' }}>
          <CheckCircle size={56} style={{ color: 'var(--color-success)' }} />
          <h4 style={{ color: 'var(--text-high)', margin: 0, fontSize: '18px' }}>{mobileMessage}</h4>
          {generatedZPL && (
            <div style={{ width: '100%', textAlign: 'left', marginTop: '20px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>CÓDIGO ETIQUETA ZEBRA (ZPL):</span>
              <pre className="zpl-preview" style={{ fontSize: '11px', maxHeight: '180px', background: 'var(--bg-inset)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', overflowY: 'auto' }}>{generatedZPL}</pre>
            </div>
          )}
        </div>
      )
    }

    switch (mobileScreen) {
      case 'menu': {
        const canEntrada = usuarioLogado ? permissoes[usuarioLogado.nivel]?.importacao !== false : false
        const canSaida = usuarioLogado ? permissoes[usuarioLogado.nivel]?.balanco !== false : false
        const canConsulta = usuarioLogado ? permissoes[usuarioLogado.nivel]?.locais !== false : false

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', justifyContent: 'center', minHeight: '70vh', padding: '10px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '26px', color: 'var(--text-high)' }}>THOTH Coletor</h2>
              <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Operação de Chão de Fábrica</span>
            </div>

            {canEntrada && (
              <button className="btn-primary" style={{ padding: '20px', borderRadius: '16px', fontSize: '18px', justifyContent: 'center' }} onClick={() => setMobileScreen('entrada')}>
                <PlusCircle size={22} /> ENTRADA DE MATERIAL
              </button>
            )}
            {canSaida && (
              <button className="btn-primary" style={{ padding: '20px', borderRadius: '16px', fontSize: '18px', justifyContent: 'center', background: 'linear-gradient(135deg, var(--color-danger), var(--color-secondary))', boxShadow: '0 4px 12px rgba(244, 63, 94, 0.2)' }} onClick={() => setMobileScreen('saida')}>
                <ArrowRightLeft size={22} /> SAÍDA / BAIXA DE MATERIAL
              </button>
            )}
            {canConsulta && (
              <button className="btn-secondary" style={{ padding: '20px', borderRadius: '16px', fontSize: '18px', justifyContent: 'center' }} onClick={() => setMobileScreen('consulta')}>
                <Search size={22} /> CONSULTA RÁPIDA
              </button>
            )}
            {!canEntrada && !canSaida && !canConsulta && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
                Seu perfil não possui permissões de acesso ao coletor.
              </div>
            )}
          </div>
        )
      }
      case 'entrada': {


        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Registrar Entrada</h3>
              <button className="btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => setMobileScreen('menu')}>Voltar</button>
            </div>

            <form onSubmit={handleMobileEntrada} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Produto *</label>
                <input 
                  className="form-input" 
                  style={{ padding: '10px 14px' }} 
                  type="text" 
                  placeholder="Selecione ou digite o material" 
                  list="produtos-datalist" 
                  value={mProdName} 
                  onChange={e => {
                    const val = sanitizeInput(e.target.value)
                    setMProdName(val)
                    const match = produtos.find(p => p.nome.toUpperCase() === val.toUpperCase())
                    if (match) {
                      setMProdId(match.id)
                      setMCusto(match.preco_compra || 0)
                    } else {
                      setMProdId('')
                    }
                  }} 
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    Código do Lote *
                    <FieldTooltip text="O identificador físico do lote recebido para rastreamento FIFO e controle de validades." />
                  </label>
                  <input className="form-input" style={{ padding: '10px 14px' }} type="text" placeholder="Ex: LOTE-TEC-99" value={mLoteCode} onChange={e => setMLoteCode(sanitizeInput(e.target.value))} required />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    Fornecedor *
                    <FieldTooltip text="Selecione o parceiro que forneceu este insumo para rastreamento de garantia." />
                  </label>
                  <input 
                    className="form-input" 
                    style={{ padding: '10px 14px' }} 
                    type="text" 
                    placeholder="Fornecedor cadastrado" 
                    list="fornecedores-datalist" 
                    value={mFornName} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setMFornName(val)
                      const match = entidades.find(ent => ent.nome.toUpperCase() === val.toUpperCase() && ent.tipo_relacao === 'fornecedor')
                      if (match) {
                        setMFornId(match.id)
                      } else {
                        setMFornId('')
                      }
                    }} 
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Preço Compra (Custo R$)</label>
                  <input className="form-input" style={{ padding: '10px 14px' }} type="number" step="any" placeholder="0.00" value={mCusto || ''} onChange={e => setMCusto(Number(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Preço Venda (+30% R$)</label>
                  <input className="form-input" style={{ padding: '10px 14px' }} type="number" step="any" placeholder="0.00" value={mVenda || ''} onChange={e => setMVenda(Number(e.target.value))} />
                </div>
              </div>

              {selectedProduct?.grupo.toUpperCase() === 'MADEIRA' ? (
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    Tipo de Madeira *
                    <FieldTooltip text="Selecione o tipo físico de madeira (PINUS, EUCALIPTO, etc.)." />
                  </label>
                  <select 
                    className="form-input" 
                    style={{ padding: '10px 14px', width: '100%' }} 
                    value={mTipoMadeira.toUpperCase()} 
                    onChange={e => setMTipoMadeira(sanitizeInput(e.target.value))}
                    required
                  >
                    {(parametros.grupos.find(g => g.nome === 'MADEIRA')?.subgrupos || []).map(m => (
                      <option key={m} value={m}>
                        {m.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: ['TECIDO', 'TNT'].includes(selectedProduct?.grupo.toUpperCase() || '') ? '1fr 1fr' : '1fr', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      Tom Específico
                      <FieldTooltip text="O banho/tingimento exato do tecido. Essencial para evitar camas/colchões bicolores." />
                    </label>
                    <input className="form-input" style={{ padding: '10px 14px' }} type="text" placeholder="Ex: Bege Cru" list="tons-datalist" value={mTom} onChange={e => setMTom(sanitizeInput(e.target.value))} />
                  </div>
                  {['TECIDO', 'TNT'].includes(selectedProduct?.grupo.toUpperCase() || '') && (
                    <div>
                      <label style={{ fontSize: '13px', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontWeight: 'bold' }}>
                        Agrupador Comercial *
                        <FieldTooltip text="Agrupa itens com variações (tom, medida, marca, etc.) sob o mesmo nome comercial para o mesmo fim prático." />
                        <span style={{ fontSize: '9px', background: 'rgba(99,102,241,0.15)', color: 'var(--color-primary)', padding: '1px 4px', borderRadius: '3px' }}>Agrupador</span>
                      </label>
                      {selectedProduct?.grupo.toUpperCase() === 'TNT' ? (
                        <input className="form-input" style={{ padding: '10px 14px' }} type="text" value="PRETO" readOnly disabled />
                      ) : (
                        <>
                          <select 
                            className="form-input" 
                            style={{ padding: '10px 14px', border: '2px solid var(--color-primary)', background: 'rgba(99, 102, 241, 0.03)', width: '100%' }} 
                            value={mCor} 
                            onChange={e => setMCor(sanitizeInput(e.target.value))}
                            required
                          >
                            {parametros.representacoesComerciais.map(rc => (
                              <option key={rc} value={rc}>
                                {rc.toUpperCase()}
                              </option>
                            ))}
                          </select>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', background: 'rgba(99, 102, 241, 0.05)', padding: '6px 10px', borderRadius: '6px', borderLeft: '3px solid var(--color-primary)', lineHeight: '1.3' }}>
                            💡 Agrupa itens com diferentes variações de características físicas para a mesma finalidade (ex: <em>Chamonix Bege Escuro e Suede Bege Claro ➜ <strong>SUEDE BEGE</strong></em>).
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedProduct?.grupo.toUpperCase() === 'MADEIRA' ? (
                <div style={{ background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                    Calculadora Automática de Madeira
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        padding: '8px',
                        fontSize: '12px',
                        justifyContent: 'center',
                        background: calcMode === 'volume_recibido' ? 'var(--badge-bg-primary)' : 'transparent',
                        borderColor: calcMode === 'volume_recibido' ? 'var(--color-primary)' : 'var(--border-color)',
                        color: calcMode === 'volume_recibido' ? 'var(--text-high)' : 'var(--text-medium)'
                      }}
                      onClick={() => setCalcMode('volume_recibido')}
                    >
                      Volume (m³)
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        padding: '8px',
                        fontSize: '12px',
                        justifyContent: 'center',
                        background: calcMode === 'pallet_dims' ? 'var(--badge-bg-primary)' : 'transparent',
                        borderColor: calcMode === 'pallet_dims' ? 'var(--color-primary)' : 'var(--border-color)',
                        color: calcMode === 'pallet_dims' ? 'var(--text-high)' : 'var(--text-medium)'
                      }}
                      onClick={() => setCalcMode('pallet_dims')}
                    >
                      Pilha Dims
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        padding: '8px',
                        fontSize: '12px',
                        justifyContent: 'center',
                        background: calcMode === 'pecas_pallets' ? 'var(--badge-bg-primary)' : 'transparent',
                        borderColor: calcMode === 'pecas_pallets' ? 'var(--color-primary)' : 'var(--border-color)',
                        color: calcMode === 'pecas_pallets' ? 'var(--text-high)' : 'var(--text-medium)'
                      }}
                      onClick={() => setCalcMode('pecas_pallets')}
                    >
                      Por Peças
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Qtd Pallets</label>
                        <input className="form-input" style={{ padding: '8px 12px' }} type="number" value={calcPallets} onChange={e => setCalcPallets(Math.max(1, Number(e.target.value)))} />
                      </div>
                      
                      {calcMode === 'pecas_pallets' && (
                        <div>
                          <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Peças Totais</label>
                          <input className="form-input" style={{ padding: '8px 12px' }} type="number" value={calcPecasTotal} onChange={e => setCalcPecasTotal(Number(e.target.value))} />
                        </div>
                      )}
                      {calcMode === 'volume_recibido' && (
                        <div>
                          <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Volume m³ Total</label>
                          <input className="form-input" style={{ padding: '8px 12px' }} type="number" step="any" value={calcVolumeTotal} onChange={e => setCalcVolumeTotal(Number(e.target.value))} />
                        </div>
                      )}
                    </div>

                    {calcMode === 'pallet_dims' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Comp Pilha(cm)</label>
                          <input className="form-input" style={{ padding: '8px 10px' }} type="number" value={calcStackL} onChange={e => setCalcStackL(Number(e.target.value))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Larg Pilha(cm)</label>
                          <input className="form-input" style={{ padding: '8px 10px' }} type="number" value={calcStackW} onChange={e => setCalcStackW(Number(e.target.value))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Alt Pilha(cm)</label>
                          <input className="form-input" style={{ padding: '8px 10px' }} type="number" value={calcStackH} onChange={e => setCalcStackH(Number(e.target.value))} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Peças:</span>
                      <strong style={{ color: 'var(--text-high)' }}>{computedTotalUnits} unidades</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Média por Pallet:</span>
                      <strong style={{ color: 'var(--text-high)' }}>{computedUnitsPerPallet} un/pallet</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Volume Total:</span>
                      <strong style={{ color: 'var(--color-primary)' }}>{computedTotalVolume.toFixed(4)} m³</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Área Total:</span>
                      <strong style={{ color: 'var(--color-success)' }}>{computedTotalArea.toFixed(2)} m²</strong>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Destino (Local) *</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input 
                        className="form-input" 
                        style={{ padding: '10px 14px', flex: 1 }} 
                        type="text" 
                        placeholder="Endereço de estoque"
                        list="locais-datalist"
                        value={mLocalCode} 
                        onChange={e => {
                          const val = sanitizeInput(e.target.value)
                          setMLocalCode(val)
                          const match = locais.find(l => l.codigo_local.toUpperCase() === val.toUpperCase())
                          if (match) {
                            setMLocalId(match.id)
                          } else {
                            setMLocalId('')
                          }
                        }}
                        required
                      />
                      <button type="button" className="btn-secondary" style={{ padding: '10px' }} onClick={() => setMobileScannerActive(true)}>
                        <QrCode size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: 'var(--text-high)' }}>Quantidade</label>
                    <input className="form-input" style={{ padding: '10px 14px' }} type="number" value={mQty} onChange={e => setMQty(Number(e.target.value))} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Destino (Local) *</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input 
                        className="form-input" 
                        style={{ padding: '10px 14px', flex: 1 }} 
                        type="text" 
                        placeholder="Endereço de estoque"
                        list="locais-datalist"
                        value={mLocalCode} 
                        onChange={e => {
                          const val = sanitizeInput(e.target.value)
                          setMLocalCode(val)
                          const match = locais.find(l => l.codigo_local.toUpperCase() === val.toUpperCase())
                          if (match) {
                            setMLocalId(match.id)
                          } else {
                            setMLocalId('')
                          }
                        }}
                        required
                      />
                      <button type="button" className="btn-secondary" style={{ padding: '10px' }} onClick={() => setMobileScannerActive(true)}>
                        <QrCode size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              )}



              <button className="btn-primary" style={{ marginTop: '14px', justifyContent: 'center', padding: '14px' }} type="submit">Salvar e Gerar Etiquetas</button>
            </form>
          </div>
        )
      }
      case 'saida': {
        const selectedLote = lotes.find(l => l.codigo_lote === mobileScannedCode)
        const selectedProd = selectedLote ? produtos.find(p => p.id === selectedLote.produto_id) : null
        
        let content = null
        if (selectedLote) {
          const selectedSaldo = saldos.find(s => s.lote_id === selectedLote.id)
          const currentQtyFisica = selectedSaldo ? selectedSaldo.quantidade_fisica : 0

          let piecesTotal = 0
          let vUnit = 0
          if (selectedProd && selectedProd.grupo.toUpperCase() === 'MADEIRA') {
            const match = selectedProd.nome.match(/(\d+)\s*[xX]\s*(\d+)\s*[xX]\s*([\d.,]+)\s*cm/)
            const L = match ? Number(match[1]) / 100 : 1.86
            const W = match ? Number(match[2]) / 100 : 1.36
            const H = match ? parseFloat(match[3].replace(',', '.')) / 100 : 0.005
            vUnit = L * W * H
            piecesTotal = vUnit > 0 ? Math.round(currentQtyFisica / vUnit) : 0
          }

          const isRebalancedCat = selectedProd && ['TECIDO', 'TNT', 'MALHA'].includes(selectedProd.grupo.toUpperCase())
          const standardRolo = 50
          const currentAvgMetric = selectedLote.peso_medio_recalculado || (selectedProd && ['TECIDO', 'TNT'].includes(selectedProd.grupo.toUpperCase()) ? standardRolo : selectedProd?.peso_medio_unidade || 1)
          const currentUnitsEst = currentAvgMetric > 0 ? currentQtyFisica / currentAvgMetric : 0

          content = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Material Identificado:</span>
                <div style={{ color: 'var(--text-high)', fontWeight: 'bold', fontSize: '16px' }}>{selectedProd?.nome}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-medium)' }}>Código: {selectedLote.codigo_lote}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-medium)' }}>
                  {selectedProd && selectedProd.grupo.toUpperCase() === 'MADEIRA' 
                    ? `Tipo de Madeira: ${selectedLote.tipo_madeira?.toUpperCase() || 'EUCALIPTO'}` 
                    : `Tom/Especificação: ${selectedLote.tom_especifico}`}
                </div>
                {getLoteSpecsString(selectedLote) && (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '4px' }}>
                    Especificações: {getLoteSpecsString(selectedLote)}
                  </div>
                )}
                <div style={{ fontSize: '13px', color: 'var(--text-high)', fontWeight: '500', marginTop: '6px' }}>
                  Saldo Atual: <span style={{ color: 'var(--color-primary)' }}>{currentQtyFisica.toFixed(2)} {selectedProd?.unidade_medida_padrao.toUpperCase()}</span>
                  {selectedProd && selectedProd.grupo.toUpperCase() === 'MADEIRA' && ` (~${piecesTotal} peças)`}
                  {isRebalancedCat && ` (~${currentUnitsEst.toFixed(1)} ${selectedProd && ['TECIDO', 'TNT'].includes(selectedProd.grupo.toUpperCase()) ? 'rolos' : 'unidades'})`}
                </div>
                {isRebalancedCat && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', padding: '4px 8px', borderRadius: '4px', marginTop: '4px' }}>
                    Média Atual do Lote: <strong>{currentAvgMetric.toFixed(2)} {selectedProd && ['TECIDO', 'TNT'].includes(selectedProd.grupo.toUpperCase()) ? 'metros/rolo' : 'kg/unidade'}</strong>
                  </div>
                )}
              </div>

              {selectedProd && selectedProd.grupo.toUpperCase() === 'MADEIRA' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '6px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                  <button 
                    type="button"
                    className="btn-danger" 
                    style={{ padding: '12px', fontSize: '14px', justifyContent: 'center', width: '100%', marginBottom: '6px' }} 
                    onClick={() => handleMobileSaida(selectedLote.codigo_lote, currentQtyFisica, 'producao')}
                  >
                    Baixar Pallet Inteiro (~{piecesTotal} un)
                  </button>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Opção A: Retirar por Peças (Unidades)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input 
                        className="form-input" 
                        type="number" 
                        placeholder="Peças a retirar" 
                        value={mCheckoutQtyUnits || ''} 
                        onChange={e => {
                          const pcs = Math.max(1, Number(e.target.value))
                          setMCheckoutQtyUnits(pcs)
                          setMCheckoutQtyVolume(Number((pcs * vUnit).toFixed(6)))
                        }} 
                        style={{ flex: 1, padding: '10px', fontSize: '14px' }} 
                      />
                      <button 
                        type="button"
                        className="btn-primary" 
                        style={{ padding: '10px 16px', fontSize: '14px' }} 
                        onClick={() => {
                          if (mCheckoutQtyUnits <= 0) return
                          if (mCheckoutQtyUnits > piecesTotal) {
                            alert(`Quantidade solicitada (${mCheckoutQtyUnits}) excede o total do pallet (${piecesTotal} peças)`)
                            return
                          }
                          const qtyM3 = mCheckoutQtyUnits * vUnit
                          handleMobileSaida(selectedLote.codigo_lote, qtyM3, 'producao')
                        }}
                      >
                        Retirar
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px', marginTop: '4px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Opção B: Retirar por Volume (m³)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input 
                        className="form-input" 
                        type="number" 
                        step="any"
                        placeholder="Volume m³ a retirar" 
                        value={mCheckoutQtyVolume || ''} 
                        onChange={e => {
                          const vol = Math.max(0, Number(e.target.value))
                          setMCheckoutQtyVolume(vol)
                          setMCheckoutQtyUnits(vUnit > 0 ? Math.round(vol / vUnit) : 0)
                        }} 
                        style={{ flex: 1, padding: '10px', fontSize: '14px' }} 
                      />
                      <button 
                        type="button"
                        className="btn-primary" 
                        style={{ padding: '10px 16px', fontSize: '14px' }} 
                        onClick={() => {
                          if (mCheckoutQtyVolume <= 0) return
                          if (mCheckoutQtyVolume > currentQtyFisica) {
                            alert(`Volume solicitado (${mCheckoutQtyVolume.toFixed(4)} m³) excede o total do pallet (${currentQtyFisica.toFixed(4)} m³)`)
                            return
                          }
                          handleMobileSaida(selectedLote.codigo_lote, mCheckoutQtyVolume, 'producao')
                        }}
                      >
                        Retirar
                      </button>
                    </div>
                  </div>
                </div>
              ) : isRebalancedCat ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '10px', marginTop: '4px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Quantidade a retirar ({selectedProd?.unidade_medida_padrao})</label>
                    <input 
                      className="form-input"
                      type="number"
                      step="any"
                      placeholder={`Ex: 50 ${selectedProd?.unidade_medida_padrao}`}
                      id="mobileSaidaQty"
                      style={{ padding: '10px', fontSize: '14px', width: '100%' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button 
                      type="button"
                      className="btn-primary" 
                      style={{ padding: '12px', fontSize: '13px', justifyContent: 'center' }}
                      onClick={() => {
                        const qInput = document.getElementById('mobileSaidaQty') as HTMLInputElement
                        const qVal = qInput ? Number(qInput.value) : 0
                        if (qVal <= 0) {
                          alert('Por favor, informe a quantidade a ser retirada.')
                          return
                        }
                        handleMobileSaida(selectedLote.codigo_lote, qVal, 'producao')
                      }}
                    >
                      Confirmar Uso
                    </button>
                    <button 
                      type="button"
                      className="btn-secondary" 
                      style={{ padding: '12px', fontSize: '13px', justifyContent: 'center', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                      onClick={() => {
                        const qInput = document.getElementById('mobileSaidaQty') as HTMLInputElement
                        const qVal = qInput ? Number(qInput.value) : 0
                        if (qVal <= 0) {
                          alert('Por favor, informe a quantidade perdida.')
                          return
                        }
                        handleMobileSaida(selectedLote.codigo_lote, qVal, 'perda')
                      }}
                    >
                      Registrar Perda
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                  <button type="button" className="btn-danger" style={{ padding: '14px 6px', fontSize: '14px', justifyContent: 'center' }} onClick={() => handleMobileSaida(selectedLote.codigo_lote, currentQtyFisica, 'producao')}>
                    Baixar Uso
                  </button>
                  <button type="button" className="btn-secondary" style={{ padding: '14px 6px', fontSize: '14px', justifyContent: 'center', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={() => {
                    const lossQty = Number(prompt('Quantidade perdida/descartada:'))
                    if (lossQty > 0) handleMobileSaida(selectedLote.codigo_lote, lossQty, 'perda')
                  }}>
                    Registrar Perda
                  </button>
                </div>
              )}
            </div>
          )
        } else {
          content = (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              Aguardando leitura de QR Code do Lote de Tecido ou Pallet de Madeira...
            </div>
          )
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Registrar Saída</h3>
              <button className="btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => setMobileScreen('menu')}>Voltar</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input className="form-input" style={{ padding: '12px 14px', fontSize: '15px' }} type="text" placeholder="Escanear Lote/Item" value={mobileScannedCode} onChange={e => setMobileScannedCode(e.target.value)} />
              <button className="btn-primary" style={{ padding: '12px' }} onClick={() => setMobileScannerActive(true)}>
                <QrCode size={22} />
              </button>
            </div>

            {content}
          </div>
        )
      }
      case 'consulta':
        const lookupLote = lotes.find(l => l.codigo_lote === mobileScannedCode)
        const lookupProd = lookupLote ? produtos.find(p => p.id === lookupLote.produto_id) : null
        const lookupSaldo = lookupLote ? saldos.find(s => s.lote_id === lookupLote.id) : null

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Consultar Item</h3>
              <button className="btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => setMobileScreen('menu')}>Voltar</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input className="form-input" style={{ padding: '12px 14px', fontSize: '15px' }} type="text" placeholder="Escanear Lote/Item" value={mobileScannedCode} onChange={e => setMobileScannedCode(e.target.value)} />
              <button className="btn-primary" style={{ padding: '12px' }} onClick={() => setMobileScannerActive(true)}>
                <QrCode size={22} />
              </button>
            </div>

            {lookupLote ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Especificação:</span>
                  <div style={{ color: 'var(--text-high)', fontWeight: 'bold', fontSize: '17px' }}>{lookupProd?.nome}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-medium)' }}>
                    {lookupProd?.grupo.toUpperCase() === 'MADEIRA' 
                      ? `Tipo de Madeira: ${lookupLote.tipo_madeira?.toUpperCase() || 'EUCALIPTO'}` 
                      : `Tom/Especificação: ${lookupLote.tom_especifico}`}
                  </div>
                  {getLoteSpecsString(lookupLote) && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '4px' }}>
                      Especificações: {getLoteSpecsString(lookupLote)}
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Local Atual:</span>
                    <div style={{ color: 'var(--color-primary)', fontWeight: 'bold', fontSize: '16px' }}>
                      {locais.find(g => g.id === lookupSaldo?.local_id)?.codigo_local || 'A1'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Saldo Atual:</span>
                    <div style={{ color: 'var(--color-success)', fontWeight: 'bold', fontSize: '16px' }}>
                      {(lookupSaldo?.quantidade_fisica || 0).toFixed(2)} {lookupProd?.unidade_medida_padrao}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '35px 0', color: 'var(--text-muted)' }}>
                Escanear lote para consultar saldo em estoque e local correspondente.
              </div>
            )}
          </div>
        )
    }
  }

  if (!usuarioLogado) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '480px',
        margin: '0 auto',
        background: 'var(--bg-card)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box'
      }}>
        <div className="glass-panel" style={{
          padding: '30px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', background: 'linear-gradient(135deg, #a855f7, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0, fontWeight: 'bold' }}>THOTH Coletor</h2>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Acesso Chão de Fábrica</span>
          </div>

          {loginErr && (
            <div style={{
              background: 'rgba(244, 63, 94, 0.12)',
              border: '1px solid rgba(244, 63, 94, 0.2)',
              color: 'var(--color-danger)',
              padding: '10px',
              borderRadius: '8px',
              fontSize: '13px',
              textAlign: 'center'
            }}>
              {loginErr}
            </div>
          )}

          <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Usuário</label>
              <input
                className="form-input"
                type="text"
                placeholder="Digite seu usuário"
                value={loginUser}
                onChange={e => setLoginUser(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Senha</label>
              <input
                className="form-input"
                type="password"
                placeholder="Digite sua senha"
                value={loginPass}
                onChange={e => setLoginPass(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn-primary" style={{ justifyContent: 'center', width: '100%', padding: '12px' }}>
              Entrar no Coletor
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', maxWidth: '480px', margin: '0 auto', background: 'var(--bg-card)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
      {/* Mobile Universal Header */}
      <header style={{ background: 'var(--header-bg)', borderBottom: '1px solid var(--border-color)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Smartphone size={20} style={{ color: 'var(--color-primary)' }} />
          <h1 style={{ fontSize: '16px', margin: 0, color: 'var(--text-high)', fontWeight: 'bold' }}>
            THOTH Coletor
          </h1>
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {usuarioLogado && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold', textTransform: 'uppercase' }}>
                {usuarioLogado.username}
              </span>
              <button
                className="btn-secondary"
                style={{ padding: '6px 10px', fontSize: '11px', color: 'var(--color-danger)', borderColor: 'rgba(244,63,94,0.15)' }}
                onClick={() => {
                  setUsuarioLogado(null)
                  setMobileScreen('menu')
                }}
              >
                Sair
              </button>
            </div>
          )}
          <button
            className="btn-secondary"
            onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
            style={{
              padding: '6px',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              borderRadius: '8px'
            }}
            title={theme === 'light' ? 'Alternar para Modo Escuro' : 'Alternar para Modo Claro'}
          >
            {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
          </button>
          <a
            href="#/"
            className="btn-secondary"
            style={{
              padding: '6px',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
            title="Voltar para PC"
          >
            <Monitor size={14} />
          </a>
        </div>
      </header>

      {/* Main viewport */}
      <main style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
        {renderMobileScreen()}
      </main>

      {/* Datalists for autocompletes */}
      <datalist id="produtos-datalist">
        {produtos.map(p => (
          <option key={p.id} value={p.nome} />
        ))}
      </datalist>
      <datalist id="locais-datalist">
        {locais.map(l => (
          <option key={l.id} value={l.codigo_local} />
        ))}
      </datalist>
      <datalist id="fornecedores-datalist">
        {entidades.filter(e => e.tipo_relacao === 'fornecedor').map(e => (
          <option key={e.id} value={e.nome} />
        ))}
      </datalist>
      <datalist id="madeiras-datalist">
        {uniqueMadeiras.map(m => (
          <option key={m} value={m} />
        ))}
      </datalist>
      <datalist id="cores-datalist">
        {uniqueCores.map(c => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id="tons-datalist">
        {uniqueTons.map(t => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <datalist id="marcas-datalist">
        {uniqueMarcas.map(m => (
          <option key={m} value={m} />
        ))}
      </datalist>

    </div>
  )
}
